import {
  Component,
  EmbeddedViewRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { auditTime, filter, map, startWith } from 'rxjs/operators';
import { FilterType } from '../../../model/const/enums';
import { FilterDefinitionItem } from '../../../model/filter-definition-item';
import { SidebarItem } from '../../../model/selectable-items';
import { FormControlExFactory } from '../../../service/factory/form-control-ex-factory.service';
import { IAutoCompleteService } from '../../autocomplete-remote/autocomplete-service.interface';
import { SelectionItem } from '../../select/select.component';

type EmbeddedContext<TModel = any> = {
  control: AbstractControl;

  autoCompleteService?: IAutoCompleteService;
  serviceOptions?: any;
  updateOnOptionsChange?: boolean;
  enableInfiniteScroll?: boolean;

  itemsSource?: SelectionItem[];
  allowMultipleSelection?: boolean;
  openAtValue?: any;
  visibleOptionCount?: number;
  placeholderText?: string;

  model?: TModel;
};

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FilterComponent implements OnDestroy, OnChanges, OnInit {
  constructor(
    private readonly _fcf: FormControlExFactory,
    fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.form = fb.group({});
    this.selectionForm = fb.group({});
  }

  @Input() filters: FilterDefinitionItem[];
  @Output() readonly change: EventEmitter<any> = new EventEmitter();
  @Input() defaultFilters: string[];
  @ViewChild('container', { read: ViewContainerRef }) container: ViewContainerRef;
  @ViewChild('date') datePickerTemplate: TemplateRef<any>;
  @ViewChild('dateTime') dateTimePickerTemplate: TemplateRef<any>;
  @ViewChild('select') dropDownTemplate: TemplateRef<any>;
  @ViewChild('remotefilter') remoteFilter: TemplateRef<any>;
  @ViewChild('remoteMultiFilter') remoteMultiFilter: TemplateRef<any>;
  @ViewChild('text') textBoxTemplate: TemplateRef<any>;
  isChangeColumnsSidebarOpen = false;

  private readonly _subscription: Subscription = new Subscription();
  private readonly _views: Record<string, EmbeddedViewRef<EmbeddedContext>> = {};
  private _lastEmittedFilterState: any;
  readonly form: FormGroup;
  get isDefined(): boolean {
    return this.filters?.length > 0;
  }
  readonly selectionForm: FormGroup;

  private _createView(def: FilterDefinitionItem): void {
    if (!def) return;

    let template: TemplateRef<any>;
    let context: EmbeddedContext;
    switch (def.filterType) {
      case FilterType.AutoComplete: {
        this.form.addControl(def.name, this._fcf.createWithOptions(def.caption, def.value, { required: def.mandatory }));
        template = this.remoteFilter;
        context = {
          control: this.form.controls[def.name],
          autoCompleteService: def.autoCompleteService,
          serviceOptions: def.serviceOptions,
          enableInfiniteScroll: def.enableInfiniteScroll,
          updateOnOptionsChange: def.updateOnOptionsChange,
        };
        break;
      }
      case FilterType.AutoCompleteMulti: {
        this.form.addControl(def.name, this._fcf.createWithOptions(def.caption, this.getMultiSelectionValue(def.value), { required: def.mandatory }));
        template = this.remoteMultiFilter;
        context = {
          control: this.form.controls[def.name],
          autoCompleteService: def.autoCompleteService,
          serviceOptions: def.serviceOptions,
          enableInfiniteScroll: def.enableInfiniteScroll,
          updateOnOptionsChange: def.updateOnOptionsChange,
          allowMultipleSelection: true,
        };
        break;
      }
      case FilterType.Selection: {
        this.form.addControl(
          def.name,
          this._fcf.createWithOptions(
            def.caption,
            def.allowMultipleSelection ? this.getMultiSelectionValue(def.value) : def.value ?? '',
            { required: def.mandatory }
          )
        );
        template = this.dropDownTemplate;
        context = {
          control: this.form.controls[def.name],
          itemsSource: def.itemsSource,
          allowMultipleSelection: def.allowMultipleSelection,
          openAtValue: def.openAtValue,
          visibleOptionCount: def.visibleOptionCount,
        };
        break;
      }
      case FilterType.LocalDate: {
        this.form.addControl(def.name, this._fcf.createWithOptions(def.caption, def.value, { required: def.mandatory }));
        template = this.datePickerTemplate;
        context = {
          control: this.form.controls[def.name],
          model: def.value,
        };
        break;
      }
      case FilterType.DateTime: {
        this.form.addControl(def.name, this._fcf.createWithOptions(def.caption, def.value, { required: def.mandatory }));
        template = this.dateTimePickerTemplate;
        context = {
          control: this.form.controls[def.name],
          model: def.value,
        };
        break;
      }
      default: {
        this.form.addControl(def.name, this._fcf.createWithOptions(def.caption, def.value, { required: def.mandatory }));
        template = this.textBoxTemplate;
        context = {
          control: this.form.controls[def.name],
          placeholderText: def.placeholderText,
        };
        break;
      }
    }
    const definitionIndex = this.filters.findIndex(item => item.name === def.name);
    const viewIndex = this.filters
      .slice(0, definitionIndex)
      .filter(item => this._views[item.name])
      .length;
    this._views[def.name] = this.container.createEmbeddedView(template, context, { index: viewIndex });
  }

  private _deleteView(name: string): void {
    if (!this.form.contains(name)) return;

    this.form.removeControl(name);
    const index = this.container.indexOf(this._views[name]);
    if (index > -1) {
      this.container.remove(index);
      let view = <EmbeddedViewRef<any>>this._views[name];
      view.detach();
      delete this._views[name];
    }
  }

  private _stateChange(state): void {
    const e = Object.assign({}, state);
    e.selectedFields = Object.keys(state);
    this._lastEmittedFilterState = this.cloneFilterState(state);
    this.change.next(e);
  }

  edit(tempDefinition: SidebarItem[]) {
    const allEqual = tempDefinition.every(tempItem => {
      const filterItem = this.filters.find(defItem => defItem.name === tempItem.name);
      return filterItem.selected === tempItem.selected;
    });

    if (allEqual) return;

    tempDefinition.forEach(col => {
      this.filters.find(x => x.name === col.name).selected = col.selected;
    });

    this.filters.forEach(item => {
      this.selectionForm.controls[item.name].patchValue(item.selected);
      this.selectionForm.markAsDirty();
    });

    this.isChangeColumnsSidebarOpen = false;
    this.applyChanges();
  }

  private _hasCreatedView: boolean;

  ngOnChanges(e: SimpleChanges) {
    if (this.filters) {
      let selectedItems = this.filters.filter(x => x.selected);

      if (!this._hasCreatedView) {
        this._hasCreatedView = true;
        for (let def of selectedItems) {
          this._createView(def);
        }
        for (let item of this.filters) {
          this.selectionForm.addControl(item.name, this._fcf.create(item.caption, item.selected));
        }
      } else {
        for (let def of selectedItems) {
          const c = this._views[def.name]?.context;
          if (c?.autoCompleteService) {
            c.enableInfiniteScroll = def.enableInfiniteScroll;
            c.serviceOptions = def.serviceOptions;
          }
        }
      }
    }
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  ngOnInit() {
    this._subscription.add(
      this.form.valueChanges
        .pipe(startWith(this.form.value))
        .pipe(auditTime(1000))
        .pipe(
          map(() => this.cloneFilterState(this.form.value)),
          filter(value => !this.sameFilterState(this._lastEmittedFilterState, value))
        )
        .subscribe(value => {
          this._stateChange(value);
        })
    );

    this._subscription.add(
      this.selectionForm.valueChanges.subscribe(value => {
        for (let key of Object.keys(value)) {
          const selected = value[key];
          if (selected && this.form.contains(key)) continue;

          if (selected && !this.form.contains(key)) {
            this._createView(this.filters.find(x => x.name === key));
          } else {
            this._deleteView(key);
          }
        }
      })
    );
  }

  private applyChanges() {
    if (this.selectionForm.dirty) {
      this._stateChange(this.form.value);
      this.selectionForm.markAsPristine();
    }
  }

  openSideBar(): void {
    this.isChangeColumnsSidebarOpen = true;
  }

  sideBarCancel(): void {
    this.isChangeColumnsSidebarOpen = false;
  }

  clearOtherFilters(activeControl: AbstractControl): void {
    if (!activeControl || !this.form) {
      return;
    }

    for (const controlName of Object.keys(this.form.controls)) {
      const control = this.form.get(controlName);
      if (!control || control === activeControl) {
        continue;
      }

      control.setValue(this.getEmptyFilterValue(controlName), { emitEvent: false });
      control.markAsDirty();
      control.updateValueAndValidity({ emitEvent: false });
      this._views[controlName]?.detectChanges();
    }

    this.form.markAsDirty();
    this.refreshFilterViews();
    this._stateChange(this.form.value);
  }

  clearFilter(control: AbstractControl): void {
    if (!control) {
      return;
    }

    const controlName = Object.keys(this.form.controls).find(name => this.form.get(name) === control);
    control.setValue(this.getEmptyFilterValue(controlName), { emitEvent: false });
    control.markAsDirty();
    control.updateValueAndValidity({ emitEvent: false });

    this.form.markAsDirty();
    this._views[controlName]?.detectChanges();
    this.refreshFilterViews();
    this._stateChange(this.form.value);
  }

  clearFilters(controlNames: string[]): boolean {
    let hasClearedFilter = false;

    for (const controlName of controlNames) {
      const definition = this.filters?.find(filter => filter.name === controlName);
      const control = this.form.get(controlName);
      const hasControlValue = control && this.hasFilterValue(control.value);
      const hasDefinitionValue = definition && this.hasFilterValue(definition.value);
      if (!hasControlValue && !hasDefinitionValue) {
        continue;
      }

      const emptyValue = this.getEmptyFilterValue(controlName);
      if (definition) {
        definition.value = emptyValue;
      }
      if (control) {
        control.setValue(emptyValue, { emitEvent: false });
        control.markAsDirty();
        control.updateValueAndValidity({ emitEvent: false });
        this._views[controlName]?.detectChanges();
      }
      hasClearedFilter = true;
    }

    if (hasClearedFilter) {
      this.form.markAsDirty();
      this.refreshFilterViews();
      this._stateChange(this.form.value);
    }

    return hasClearedFilter;
  }

  removeFilter(name: string): void {
    this._deleteView(name);
    if (this.selectionForm.contains(name)) {
      this.selectionForm.removeControl(name, { emitEvent: false });
    }
    this.refreshFilterViews();
  }

  private getEmptyFilterValue(controlName: string): any {
    const isMultiSelect = this._views[controlName]?.context?.allowMultipleSelection
      ?? this.filters?.find(filter => filter.name === controlName)?.allowMultipleSelection;
    if (isMultiSelect) {
      return [];
    }

    return undefined;
  }

  private getMultiSelectionValue(value: unknown): unknown[] {
    return (Array.isArray(value) ? value : [value]).filter(item => item !== null && item !== undefined && item !== '');
  }

  private hasFilterValue(value: unknown): boolean {
    return Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== '';
  }

  private sameFilterState(left: any, right: any): boolean {
    if (!left || !right) {
      return left === right;
    }

    const keys = Object.keys(right);
    if (Object.keys(left).length !== keys.length) {
      return false;
    }

    return keys.every(key => this.sameFilterValue(left[key], right[key]));
  }

  private sameFilterValue(left: unknown, right: unknown): boolean {
    if (Array.isArray(left) || Array.isArray(right)) {
      return Array.isArray(left) && Array.isArray(right)
        && left.length === right.length
        && left.every((value, index) => value === right[index]);
    }

    if (left instanceof Object && right instanceof Object) {
      return left.toString() === right.toString();
    }

    return left === right;
  }

  private cloneFilterState(state: any): any {
    return Object.fromEntries(Object.entries(state).map(([key, value]) => [key, Array.isArray(value) ? [...value] : value]));
  }

  private refreshFilterViews(): void {
    for (const view of Object.values(this._views)) {
      view.detectChanges();
    }

    this.cdr.detectChanges();
  }
}
